import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    Card,
    Divider,
    message,
    Select,
    Row,
    Col,
    InputNumber
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { freightAPI, exchangeRateAPI } from '../api';
import countryList from 'react-select-country-list';

const { Option } = Select;

const FreightDialog = ({ visible, editingTemplate, onClose, onSaveSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [destinations, setDestinations] = useState([]);
    const [availableCurrencies, setAvailableCurrencies] = useState([]);
    const [jsonModalVisible, setJsonModalVisible] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [exampleModalVisible, setExampleModalVisible] = useState(false);

    // 获取国家列表
    const countries = countryList();
    const countryOptions = countries.getData();

    // 获取可用货币列表
    useEffect(() => {
        fetchAvailableCurrencies();
    }, []);

    const fetchAvailableCurrencies = async () => {
        try {
            const response = await exchangeRateAPI.getAvailableCurrencies();
            setAvailableCurrencies(response.data);
        } catch (error) {
            
            // 如果获取失败，至少提供USD
            setAvailableCurrencies([{ code: 'USD', rate: 1.0 }]);
        }
    };

    useEffect(() => {
        if (visible) {
            if (editingTemplate) {
                // 编辑模式：从数据中恢复目的地配置
                const templateData = editingTemplate.data || {};
                const destinationList = Object.keys(templateData).map(countryCode => {
                    const etaString = templateData[countryCode].eta || '';
                    let minDays = '';
                    let maxDays = '';
                    
                    // 解析 "数字-数字" 格式的 eta
                    if (etaString && typeof etaString === 'string') {
                        const etaParts = etaString.split('-');
                        if (etaParts.length === 2) {
                            const min = parseInt(etaParts[0].trim());
                            const max = parseInt(etaParts[1].trim());
                            if (!isNaN(min)) minDays = min;
                            if (!isNaN(max)) maxDays = max;
                        }
                    }
                    
                    return {
                        id: Date.now() + Math.random(),
                        countryCode,
                        minDays,
                        maxDays,
                        weightRanges: Object.keys(templateData[countryCode].weight || {}).map(weight => ({
                            id: Date.now() + Math.random(),
                            weight: parseInt(weight),
                            prePacket: templateData[countryCode].weight[weight].pre_packet || '',
                            preKg: templateData[countryCode].weight[weight].pre_kg || '',
                            currency: templateData[countryCode].weight[weight].currency || 'USD'
                        }))
                    };
                });

                setDestinations(destinationList);
                form.setFieldsValue({
                    name: editingTemplate.name,
                });
            } else {
                // 新建模式：重置数据
                setDestinations([]);
                form.resetFields();
            }
        }
    }, [visible, editingTemplate, form]);

    const handleAddDestination = () => {
        const newDestination = {
            id: Date.now(),
            countryCode: '',
            minDays: '',
            maxDays: '',
            weightRanges: []
        };
        setDestinations([...destinations, newDestination]);
    };

    const handleRemoveDestination = (destinationId) => {
        setDestinations(destinations.filter(dest => dest.id !== destinationId));
    };

    const handleDestinationChange = (destinationId, field, value) => {
        setDestinations(destinations.map(dest =>
            dest.id === destinationId
                ? { ...dest, [field]: value }
                : dest
        ));
    };

    const handleAddWeightRange = (destinationId) => {
        const newWeightRange = {
            id: Date.now() + Math.random(),
            weight: '',
            prePacket: '',
            preKg: '',
            currency: 'USD'
        };

        setDestinations(destinations.map(dest =>
            dest.id === destinationId
                ? { ...dest, weightRanges: [...dest.weightRanges, newWeightRange] }
                : dest
        ));
    };

    const handleRemoveWeightRange = (destinationId, weightRangeId) => {
        setDestinations(destinations.map(dest =>
            dest.id === destinationId
                ? { ...dest, weightRanges: dest.weightRanges.filter(wr => wr.id !== weightRangeId) }
                : dest
        ));
    };

    const handleWeightRangeChange = (destinationId, weightRangeId, field, value) => {
        setDestinations(destinations.map(dest =>
            dest.id === destinationId
                ? {
                    ...dest,
                    weightRanges: dest.weightRanges.map(wr =>
                        wr.id === weightRangeId
                            ? { ...wr, [field]: value }
                            : wr
                    )
                }
                : dest
        ));
    };

    const handleSubmit = async (values) => {
        // 验证必填字段
        if (!values.name || values.name.trim() === '') {
            message.error('请输入渠道名称');
            return;
        }

        if (destinations.length === 0) {
            message.error('请至少添加一个目的地');
            return;
        }

        // 验证目的地数据
        for (const dest of destinations) {
            if (!dest.countryCode) {
                message.error('请选择目的地国家/地区');
                return;
            }
            if (!dest.minDays || !dest.maxDays) {
                message.error('请输入最短天数和最长天数');
                return;
            }
            if (dest.minDays <= 0 || dest.maxDays <= 0) {
                message.error('天数必须大于0');
                return;
            }
            if (dest.minDays > dest.maxDays) {
                message.error('最短天数不能大于最长天数');
                return;
            }
            if (dest.weightRanges.length === 0) {
                message.error('请为每个目的地至少添加一个重量段');
                return;
            }

            for (const wr of dest.weightRanges) {
                if (!wr.weight || wr.weight <= 0) {
                    message.error('请输入有效的重量段');
                    return;
                }
                // 费用可以为0或空（表示免费），不强制要求输入
            }
        }

        // 构建提交数据
        const freightData = {};
        destinations.forEach(dest => {
            const weightData = {};
            dest.weightRanges.forEach(wr => {
                weightData[wr.weight.toString()] = {
                    pre_packet: (wr.prePacket || 0).toString(),
                    pre_kg: (wr.preKg || 0).toString(),
                    currency: wr.currency || 'USD'
                };
            });

            freightData[dest.countryCode] = {
                eta: `${dest.minDays}-${dest.maxDays}`,
                weight: weightData
            };
        });

        const submitData = {
            name: values.name.trim(),
            data: freightData,
        };

        try {
            setLoading(true);
            if (editingTemplate && editingTemplate.id) {
                await freightAPI.update(editingTemplate.id, submitData);
                message.success('更新运费模板成功');
            } else {
                await freightAPI.create(submitData);
                message.success('创建运费模板成功');
            }
            onSaveSuccess();
        } catch (error) {
            message.error(editingTemplate && editingTemplate.id ? '更新运费模板失败' : '创建运费模板失败');
        } finally {
            setLoading(false);
        }
    };

    const getCountryName = (countryCode) => {
        const country = countryOptions.find(option => option.value === countryCode);
        return country ? country.label : countryCode;
    };

    const getModalTitle = () => {
        if (editingTemplate && editingTemplate.id) {
            return '编辑运费模板';
        } else if (editingTemplate && !editingTemplate.id) {
            return '复制运费模板';
        } else {
            return '新建运费模板';
        }
    };

    // 将当前表单数据转换为JSON格式
    const convertDestinationsToJson = () => {
        const freightData = {};
        destinations.forEach(dest => {
            if (dest.countryCode) {
                const weightData = {};
                dest.weightRanges.forEach(wr => {
                    if (wr.weight) {
                        weightData[wr.weight.toString()] = {
                            pre_packet: (wr.prePacket || 0).toString(),
                            pre_kg: (wr.preKg || 0).toString(),
                            currency: wr.currency || 'USD'
                        };
                    }
                });

                if (Object.keys(weightData).length > 0) {
                    freightData[dest.countryCode] = {
                        eta: `${dest.minDays || ''}-${dest.maxDays || ''}`,
                        weight: weightData
                    };
                }
            }
        });
        return freightData;
    };

    // 将JSON数据转换为表单格式
    const convertJsonToDestinations = (jsonData) => {
        try {
            const parsedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
            const destinationList = Object.keys(parsedData).map(countryCode => {
                const etaString = parsedData[countryCode].eta || '';
                let minDays = '';
                let maxDays = '';
                
                // 解析 "数字-数字" 格式的 eta
                if (etaString && typeof etaString === 'string') {
                    const etaParts = etaString.split('-');
                    if (etaParts.length === 2) {
                        const min = parseInt(etaParts[0].trim());
                        const max = parseInt(etaParts[1].trim());
                        if (!isNaN(min)) minDays = min;
                        if (!isNaN(max)) maxDays = max;
                    }
                }
                
                return {
                    id: Date.now() + Math.random(),
                    countryCode,
                    minDays,
                    maxDays,
                    weightRanges: Object.keys(parsedData[countryCode].weight || {}).map(weight => ({
                        id: Date.now() + Math.random(),
                        weight: parseInt(weight),
                        prePacket: parsedData[countryCode].weight[weight].pre_packet || '',
                        preKg: parsedData[countryCode].weight[weight].pre_kg || '',
                        currency: parsedData[countryCode].weight[weight].currency || 'USD'
                    }))
                };
            });
            return destinationList;
        } catch (error) {
            throw new Error('JSON格式不正确');
        }
    };

    // 打开JSON编辑器
    const handleOpenJsonEditor = () => {
        const jsonData = convertDestinationsToJson();
        setJsonText(JSON.stringify(jsonData, null, 2));
        setJsonModalVisible(true);
    };

    // 确认JSON编辑
    const handleJsonConfirm = () => {
        try {
            const newDestinations = convertJsonToDestinations(jsonText);
            setDestinations(newDestinations);
            setJsonModalVisible(false);
            message.success('JSON数据导入成功');
        } catch (error) {
            message.error(error.message || 'JSON格式错误，请检查格式');
        }
    };

    // 取消JSON编辑
    const handleJsonCancel = () => {
        setJsonModalVisible(false);
        setJsonText('');
    };

    // 打开示例JSON
    const handleOpenExample = () => {
        setExampleModalVisible(true);
    };

    // 关闭示例JSON
    const handleCloseExample = () => {
        setExampleModalVisible(false);
    };

    // 处理文件导入
    const handleFileImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const fileContent = e.target.result;
                // 验证JSON格式
                JSON.parse(fileContent);
                
                // 转换为表单格式并更新
                const newDestinations = convertJsonToDestinations(fileContent);
                setDestinations(newDestinations);
                message.success('JSON文件导入成功');
            } catch (error) {
                message.error('文件格式错误，请确保是有效的JSON文件');
            }
        };
        reader.readAsText(file);
        
        // 清空input值，允许重复选择同一文件
        event.target.value = '';
    };

     return (
         <>
         <Modal
             title={getModalTitle()}
             open={visible}
             onCancel={onClose}
             onOk={() => form.submit()}
             okText="保存"
             cancelText="取消"
             width={800}
             confirmLoading={loading}
             destroyOnClose
         >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    label="渠道名称"
                    name="name"
                    rules={[
                        { required: true, message: '请输入渠道名称' },
                        { max: 100, message: '渠道名称不能超过100个字符' },
                    ]}
                >
                    <Input placeholder="请输入渠道名称" />
                </Form.Item>

                <Divider orientation="left">
                    目的地配置
                    <div style={{ display: 'inline-flex', gap: '8px', marginLeft: 16 }}>
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={handleOpenJsonEditor}
                            style={{ padding: '4px 8px' }}
                        >
                            JSON编辑
                        </Button>
                        <Button
                            type="link"
                            onClick={() => document.getElementById('jsonFileInput').click()}
                            style={{ padding: '4px 8px' }}
                        >
                            从文件导入JSON
                        </Button>
                        <Button
                            type="link"
                            onClick={handleOpenExample}
                            style={{ padding: '4px 8px' }}
                        >
                            JSON说明/AI prompt
                        </Button>
                    </div>
                </Divider>

                {/* 隐藏的文件输入 */}
                <input
                    id="jsonFileInput"
                    type="file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={handleFileImport}
                />


                {destinations.map(destination => (
                    <Card
                        key={destination.id}
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={`目的地: ${destination.countryCode ? getCountryName(destination.countryCode) : '未选择'}`}
                        extra={
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemoveDestination(destination.id)}
                            >
                                删除
                            </Button>
                        }
                    >
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <div style={{ marginBottom: 8 }}>国家/地区</div>
                                <Select
                                    placeholder="选择国家/地区"
                                    value={destination.countryCode}
                                    onChange={(value) => handleDestinationChange(destination.id, 'countryCode', value)}
                                    style={{ width: '100%' }}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {countryOptions.map(country => (
                                        <Option key={country.value} value={country.value}>
                                            {country.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col span={12}>
                                <div style={{ marginBottom: 8 }}>时效（天）</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <InputNumber
                                        placeholder="最短天数"
                                        value={destination.minDays}
                                        onChange={(value) => handleDestinationChange(destination.id, 'minDays', value)}
                                        style={{ flex: 1 }}
                                        min={1}
                                        precision={0}
                                    />
                                    <span style={{ color: '#999', fontSize: '14px' }}>-</span>
                                    <InputNumber
                                        placeholder="最长天数"
                                        value={destination.maxDays}
                                        onChange={(value) => handleDestinationChange(destination.id, 'maxDays', value)}
                                        style={{ flex: 1 }}
                                        min={1}
                                        precision={0}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div style={{ marginBottom: 8 }}>重量段配置（单位：g）</div>

                        {destination.weightRanges.map(weightRange => (
                            <Row key={weightRange.id} gutter={8} style={{ marginBottom: 8 }}>
                                <Col span={5}>
                                    <InputNumber
                                        placeholder="重量(g)"
                                        value={weightRange.weight}
                                        onChange={(value) => handleWeightRangeChange(destination.id, weightRange.id, 'weight', value)}
                                        style={{ width: '100%' }}
                                        min={1}
                                    />
                                </Col>
                                <Col span={5}>
                                    <InputNumber
                                        placeholder="每包裹挂号费"
                                        value={weightRange.prePacket}
                                        onChange={(value) => handleWeightRangeChange(destination.id, weightRange.id, 'prePacket', value)}
                                        style={{ width: '100%' }}
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                    />
                                </Col>
                                <Col span={5}>
                                    <InputNumber
                                        placeholder="每kg费用"
                                        value={weightRange.preKg}
                                        onChange={(value) => handleWeightRangeChange(destination.id, weightRange.id, 'preKg', value)}
                                        style={{ width: '100%' }}
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                    />
                                </Col>
                                <Col span={4}>
                                    <Select
                                        placeholder="货币"
                                        value={weightRange.currency}
                                        onChange={(value) => handleWeightRangeChange(destination.id, weightRange.id, 'currency', value)}
                                        style={{ width: '100%' }}
                                    >
                                        {availableCurrencies.map(currency => (
                                            <Option key={currency.code} value={currency.code}>
                                                {currency.code}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={5}>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleRemoveWeightRange(destination.id, weightRange.id)}
                                        size="small"
                                    >
                                        删除
                                    </Button>
                                </Col>
                            </Row>
                        ))}

                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => handleAddWeightRange(destination.id)}
                            size="small"
                        >
                            添加重量段
                        </Button>
                    </Card>
                ))}
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddDestination}
                        style={{ width: '100%' }}
                    >
                        新建目的地
                    </Button>
                </div>
            </Form>
        </Modal>

        {/* JSON编辑模态框 */}
        <Modal
            title="JSON编辑"
            open={jsonModalVisible}
            onOk={handleJsonConfirm}
            onCancel={handleJsonCancel}
            okText="确定"
            cancelText="取消"
            width={800}
            style={{ top: 20 }}
            zIndex={1100}
        >
            <div style={{ marginBottom: 16 }}>
                <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                    在下方文本框中编辑JSON数据。
                </p>
            </div>
            <Input.TextArea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder="请输入JSON数据"
                rows={20}
                style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
            />
        </Modal>

        {/* 示例JSON模态框 */}
        <Modal
            title="JSON说明/AI prompt"
            open={exampleModalVisible}
            onCancel={handleCloseExample}
            footer={[
                <Button key="close" onClick={handleCloseExample}>
                    关闭
                </Button>
            ]}
            width={700}
            zIndex={1200}
        >
            <div>

                <h4 style={{ color: '#666', fontSize: '14px', fontWeight: 'normal', margin: '0 0 16px 0' }}>把下面这段话复制给ChatGPT之类的AI，然后上传物流商的报价文件（Excel之类的），用AI来生成JSON。</h4>
                <div style={{ marginBottom: '16px' }}>

                    <div style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '16px', 
                        borderRadius: '4px', 
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                        fontSize: '12px',
                        overflow: 'auto',
                        border: '1px solid #d9d9d9'
                    }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`附件为物流商给的报价，请帮按以下JSON格式整理:
{
  "US": {
    "eta": "3-6",
    "weight": {
      "500": {
        "pre_packet": "3",
        "pre_kg": "0",
        "currency": "CNY"
      },
      "1000": {
        "pre_packet": "2",
        "pre_kg": "1",
        "currency": "CNY"
      },
      "10000": {
        "pre_packet": "8",
        "pre_kg": "8",
        "currency": "CNY"
      }
    }
  },
  "GB": {
    "eta": "4-8",
    "weight": {
      "1000": {
        "pre_packet": "20",
        "pre_kg": "1",
        "currency": "CNY"
      },
      "2000": {
        "pre_packet": "5",
        "pre_kg": "5",
        "currency": "CNY"
      }
    }
  }
}
JSON解释说明：第一层的"US" "GB"为目的地（ISO_3166-1国家代码），里面的"eta"为时效（格式为"数字-数字"，意为时效大概多少天到多少天），“weight”里面的每个key是每个重量段的数值（单位为克），然后它的object里面为该重量段的具体费用信息：“pre_packet”是挂号费，即每单的一个基础价格（有的路线不一定有挂号费），“pre_kg”为每公斤的价格，"currency"为货币（3个字母的货币代码，注意，人民币用CNY而不是RMB）。给出的附件中可能包含多条线路，如果有多条线路，请给出多个JSON（每条线路一个JSON）。
`}
                        </pre>
                    </div>
                </div>
                
            </div>
        </Modal>
        </>
    );
};

export default FreightDialog;
